import React, { useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Collapsible from "react-collapsible";
import { toast } from "react-toastify";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import Loader from "../Loader"; // Adjust path if different
import { useNavigate } from "react-router-dom";

import "../modal.css";
import "react-datepicker/dist/react-datepicker.css";
import { Gender, INCIDENT_TYPE, TREATMENT_TYPES } from "../enum";


const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  middleName: Yup.string().nullable(),
  lastName: Yup.string().required("Last name is required"),
  age: Yup.number().nullable(),
  gender: Yup.number().required("Gender is required"),
  incidentType: Yup.number().required("Incident Type is required"),

  firstResponderTreatments: Yup.number().required("Select a first responder treatment"),
  ambulanceTreatments: Yup.number().required("Select an ambulance treatment"),

  firstResponderOtherTreatment: Yup.string().when("firstResponderTreatments", {
    is: (val) => val == TREATMENT_TYPES.OTHER,
    then: Yup.string().required("Please specify the 'other' treatment"),
    otherwise: Yup.string().nullable(),
  }),

  ambulanceOtherTreatment: Yup.string().when("ambulanceTreatments", {
    is: (val) => val == TREATMENT_TYPES.OTHER,
    then: Yup.string().required("Please specify the 'other' treatment"),
    otherwise: Yup.string().nullable(),
  }),

  patientPhoneNo: Yup.string().matches(/^\+251\d{9}$|^0\d{9}$|^01\d{8}$/, "Invalid phone number").nullable(),
  incidentTypeOther: Yup.string().nullable(),

});

function FilePreview({ fileInfo }) {
  if (!fileInfo?.url || !fileInfo?.type) {
    return <p>No preview yet</p>;
  }

  if (fileInfo.type.startsWith("image/")) {
    return (
      <img
        src={fileInfo.url}
        alt="file preview"
        style={{ maxWidth: "300px", maxHeight: "300px" }}
      />
    );
  }

  if (fileInfo.type === "application/pdf") {
    return (
      <embed
        src={fileInfo.url}
        type="application/pdf"
        width="100%"
        height="600px"
      />
    );
  }

  return (
    <p>
      Preview not available.{" "}
      <a href={fileInfo.url} target="_blank" rel="noreferrer">
        Click here to open file in new tab
      </a>
    </p>
  );
}

const EditPatient = ({ showModal, hideModal, id, data, getPatients }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [previewFiles, setPreviewFiles] = useState({
    patientPhoto: null,
    patientID: null,
  });


  const onSubmit = async (values) => {
    setLoading(true);
    setErrorMsg(null);

    try {
      const bodyFormData = new FormData();
      for (let key in values) {
        bodyFormData.append(key, values[key]);
      }

      await axios.put(`/patients/${id}`, bodyFormData);

      toast.success("Patient info updated.", {
        position: "top-right",
        autoClose: 1000,
      });

      setLoading(false);
      hideModal();
      getPatients();
    } catch (error) {
      setLoading(false);
      const errMsg = error?.response?.data?.msg || "Something went wrong...";
      setErrorMsg(errMsg);

      toast.error(errMsg, {
        position: "top-right",
        autoClose: 1000,
      });
    }
  };


  const handlePreview = async (fieldName) => {
    const currentValue = values[fieldName];

    if (currentValue instanceof File) {
      const localPreviewURL = URL.createObjectURL(currentValue);
      setPreviewFiles((prev) => ({
        ...prev,
        [fieldName]: {
          url: localPreviewURL,
          type: currentValue.type,
        },
      }));
      return;
    }

    if (typeof currentValue === "string") {
      let fileURL = "";

      if (currentValue.startsWith("http://") || currentValue.startsWith("https://")) {
        fileURL = currentValue;
      } else if (currentValue.startsWith("/")) {
        const backendHost = process.env.REACT_APP_BASE_URL;
        fileURL = backendHost + currentValue;
      }

      if (fileURL) {
        try {
          const token = localStorage.getItem("d4uToken");
          const response = await axios.get(fileURL, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          });

          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          const blobURL = URL.createObjectURL(blob);

          setPreviewFiles((prev) => ({
            ...prev,
            [fieldName]: {
              url: blobURL,
              type: blob.type,
            },
          }));
        } catch (err) {
          console.error("Preview fetch error:", err);
        }
      }
    }
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    submitForm,
  } = useFormik({
    initialValues: {
      firstName: data?.firstName || "",
      middleName: data?.middleName || "",
      lastName: data?.lastName || "",
      age: data?.age || "",
      gender: data?.gender || "",
      incidentType: data?.incidentType || "",
      firstResponderTreatments: data?.firstResponderTreatments || "",
      ambulanceTreatments: data?.ambulanceTreatments || "",
      firstResponderOtherTreatment: data?.firstResponderOtherTreatment || "",
      ambulanceOtherTreatment: data?.ambulanceOtherTreatment || "",
      patientPhoneNo: data?.patientPhoneNo || "",
      incidentTypeOther: data?.incidentTypeOther || "",

      patientPhoto: data?.patientPhoto || "",
      patientID: data?.patientID || "",
    },
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <form onSubmit={handleSubmit} autoComplete="off" encType="multipart/form-data">
      <Modal style={{marginTop: "60px"}} show={showModal} onHide={hideModal} backdrop={false}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Patient</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ position: "relative" }}>
          {/* Loader */}
          {loading && (
            <div
              style={{
                position: "absolute",
                zIndex: "1000",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <Loader />
            </div>
          )}

          <div className="row">
            <div className="col-md-6">

              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={values.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.firstName && touched.firstName
                      ? "form-control input-error"
                      : "form-control"
                  }
                />
                {errors.firstName && touched.firstName && (
                  <p className="error">{errors.firstName}</p>
                )}
              </div>


              <div className="form-group">
                <label>Middle Name</label>
                <input
                  type="text"
                  name="middleName"
                  value={values.middleName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.middleName && touched.middleName
                      ? "form-control input-error"
                      : "form-control"
                  }
                />
                {errors.middleName && touched.middleName && (
                  <p className="error">{errors.middleName}</p>
                )}
              </div>

              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={values.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.lastName && touched.lastName
                      ? "form-control input-error"
                      : "form-control"
                  }
                />
                {errors.lastName && touched.lastName && (
                  <p className="error">{errors.lastName}</p>
                )}
              </div>

              <div className="form-group">
                <label>Age</label>
                <input
                  type="number"
                  name="age"
                  value={values.age}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.age && touched.age
                      ? "form-control input-error"
                      : "form-control"
                  }
                />
                {errors.age && touched.age && (
                  <p className="error">{errors.age}</p>
                )}
              </div>
            </div>

            <div className="col-md-6">


              <div className="form-group">
                <label>Gender</label>
                <select
                  name="gender"
                  value={values.gender}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.gender && touched.gender
                      ? "form-control input-error"
                      : "form-control"
                  }
                >
                  <option value="">Select Gender</option>
                  {Object.entries(Gender).map(([key, value]) => (
                    <option key={value} value={value}>
                      {key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
                {errors.gender && touched.gender && <p className="error">{errors.gender}</p>}
              </div>



              <div className="form-group">
                <label>Incident Type</label>
                <select
                  name="incidentType"
                  value={values.incidentType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.incidentType && touched.incidentType
                      ? "form-control input-error"
                      : "form-control"
                  }
                >
                  <option value="">Select Incident Type</option>
                  {Object.entries(INCIDENT_TYPE).map(([key, value]) => (
                    <option key={value} value={value}>
                      {key
                        .split("_")
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(" ")}
                    </option>
                  ))}
                </select>
                {errors.incidentType && touched.incidentType && (
                  <p className="error">{errors.incidentType}</p>
                )}
              </div>


              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  name="patientPhoneNo"
                  value={values.patientPhoneNo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.patientPhoneNo && touched.patientPhoneNo
                      ? "form-control input-error"
                      : "form-control"
                  }
                />
                {errors.patientPhoneNo && touched.patientPhoneNo && (
                  <p className="error">{errors.patientPhoneNo}</p>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">


              <div className="form-group">
                <label>First Responder Treatments</label>
                <select
                  name="firstResponderTreatments"
                  value={values.firstResponderTreatments}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.firstResponderTreatments && touched.firstResponderTreatments
                      ? "form-control input-error"
                      : "form-control"
                  }
                >
                  <option value="">Select Treatment</option>
                  {Object.entries(TREATMENT_TYPES).map(([key, value]) => (
                    <option key={value} value={value}>
                      {key
                        .split("_")
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(" ")}
                    </option>
                  ))}
                </select>
                {errors.firstResponderTreatments && touched.firstResponderTreatments && (
                  <p className="error">{errors.firstResponderTreatments}</p>
                )}
              </div>

              {Number(values.firstResponderTreatments) === TREATMENT_TYPES.OTHER && (
                <div className="form-group">
                  <label>First Responder Other Treatment</label>
                  <input
                    type="text"
                    name="firstResponderOtherTreatment"
                    value={values.firstResponderOtherTreatment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.firstResponderOtherTreatment && touched.firstResponderOtherTreatment
                        ? "form-control input-error"
                        : "form-control"
                    }
                  />
                  {errors.firstResponderOtherTreatment && touched.firstResponderOtherTreatment && (
                    <p className="error">{errors.firstResponderOtherTreatment}</p>
                  )}
                </div>
              )}
            </div>

            <div className="col-md-6">


              <div className="form-group">
                <label>Ambulance Treatments</label>
                <select
                  name="ambulanceTreatments"
                  value={values.ambulanceTreatments}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    errors.ambulanceTreatments && touched.ambulanceTreatments
                      ? "form-control input-error"
                      : "form-control"
                  }
                >
                  <option value="">Select Treatment</option>
                  {Object.entries(TREATMENT_TYPES).map(([key, value]) => (
                    <option key={value} value={value}>
                      {key
                        .split("_")
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(" ")}
                    </option>
                  ))}
                </select>
                {errors.ambulanceTreatments && touched.ambulanceTreatments && (
                  <p className="error">{errors.ambulanceTreatments}</p>
                )}
              </div>


              {Number(values.ambulanceTreatments) === TREATMENT_TYPES.OTHER && (
                <div className="form-group">
                  <label>Ambulance Other Treatment</label>
                  <input
                    type="text"
                    name="ambulanceOtherTreatment"
                    value={values.ambulanceOtherTreatment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors.ambulanceOtherTreatment && touched.ambulanceOtherTreatment
                        ? "form-control input-error"
                        : "form-control"
                    }
                  />
                  {errors.ambulanceOtherTreatment && touched.ambulanceOtherTreatment && (
                    <p className="error">{errors.ambulanceOtherTreatment}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              {Number(values.incidentType) === INCIDENT_TYPE.OTHER && (
                <div className="form-group">
                  <label>Incident Type (Other)</label>
                  <input
                    type="text"
                    name="incidentTypeOther"
                    value={values.incidentTypeOther}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label>Patient Photo</label>
                {values.patientPhoto && (
                  <Collapsible
                    trigger={
                      <i
                        className="fas fa-eye success"
                        style={{ cursor: "pointer", marginLeft: "10px" }}
                        onClick={() => handlePreview("patientPhoto")}
                      >
                        {" "}View
                      </i>
                    }
                  >
                    <FilePreview fileInfo={previewFiles.patientPhoto} />
                  </Collapsible>
                )}
                <div className="input-group">
                  <div className="custom-file">
                    <input
                      type="file"
                      name="patientPhoto"
                      accept="image/*"
                      onChange={(e) => {
                        setFieldValue("patientPhoto", e.currentTarget.files[0]);
                      }}
                      onBlur={handleBlur}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label>Patient ID (PDF or Image)</label>
                {values.patientID && (
                  <Collapsible
                    trigger={
                      <i
                        className="fas fa-eye success"
                        style={{ cursor: "pointer", marginLeft: "10px" }}
                        onClick={() => handlePreview("patientID")}
                      >
                        {" "}View
                      </i>
                    }
                  >
                    <FilePreview fileInfo={previewFiles.patientID} />
                  </Collapsible>
                )}
                <div className="input-group">
                  <div className="custom-file">
                    <input
                      type="file"
                      name="patientID"
                      accept="application/pdf, image/*"
                      onChange={(e) => {
                        setFieldValue("patientID", e.currentTarget.files[0]);
                      }}
                      onBlur={handleBlur}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {errorMsg && <h5 className="error">{errorMsg}</h5>}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={hideModal}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={submitForm}
            type="submit"
            disabled={isSubmitting || loading}
          >
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </form>
  );
};

export default EditPatient;
